/**
 * FRIDGE-796 — accessible names for product-card links.
 *
 * WCAG 2.4.4 Link Purpose: one accessible name must not resolve to two URLs.
 * Product cards break that rule on their own, without anyone writing a bug: two
 * different rows can carry the SAME manufacturer title (the ZLINE Autograph
 * 72 in. pair, the Cafe CIC36LP2VS1 / CIC36RP2VS1 pair), and every card's CTA
 * says the same two words — "Get Deal" on 23 links of one page, measured
 * 09/05/2026 on /tools/refrigerator-size-calculator.
 *
 * The disambiguator is the FIN the product href already encodes. It is our own
 * public identifier, printed on cards elsewhere on the site, so nothing is
 * announced that a reader cannot otherwise see, and it is unique by
 * construction — a name can only ever be SPLIT by adding it, never merged.
 *
 * Every name here puts the visible words FIRST (WCAG 2.5.3 Label in Name).
 */
import { extractFinFromSlug } from '@/lib/url-format'

/** The FIN a product URL encodes (`/products/<slug>-fin0000652` → `FIN-0000652`), else null. */
export function finFromProductHref(href: string | null | undefined): string | null {
  if (!href) return null
  const path = href.split('?')[0].split('#')[0]
  const last = path.split('/').filter(Boolean).pop()
  return extractFinFromSlug(last ?? null)
}

/**
 * What tells two same-titled rows apart. An `identifier` the caller passes wins
 * — it is the row's own model or minted number, which reads better than a
 * URL-derived one — and otherwise the FIN the href encodes. Filter and part
 * URLs carry no number in the slug, so those callers pass the identifier.
 */
export function productCardQualifier(href: string | null | undefined, identifier?: string | null): string | null {
  const own = (identifier ?? '').trim()
  if (own) return own
  return finFromProductHref(href)
}

/**
 * The identifier a card should pass when its URL carries no FIN — filter and
 * part rows (`/filters/<slug>`, `/parts/<slug>`). Null for appliance rows, whose
 * href already encodes the FIN (the better qualifier). The row's own model
 * first, the precedent the editorial filter hubs set (FRIDGE-796); else its
 * FFN or FPN, which are unique by construction.
 *
 * Found 09/19/2026 by the axe gate on /brands/lg: two LG filters share the
 * manufacturer title "LG 200 Gallon Capacity Replacement Refrigerator Water
 * Filter", and the brand page's cards passed no identifier, so one accessible
 * name resolved to two URLs (5 such names against a ceiling of 4).
 */
export function nonFinRowIdentifier(
  href: string | null | undefined,
  row: { model?: string | null; ffn?: string | null; fpn?: string | null },
): string | null {
  if (finFromProductHref(href)) return null
  const pick = [row.model, row.ffn, row.fpn].map((v) => (v ?? '').trim()).find((v) => v !== '')
  return pick || null
}

/**
 * The name of a card link whose visible content is the product title (the title
 * link) or nothing at all (the image link, named after the title). `suffix` is
 * the site tag some image links carry.
 */
export function productCardLinkName(
  title: string,
  href: string | null | undefined,
  suffix = '',
  identifier?: string | null,
): string {
  const q = productCardQualifier(href, identifier)
  const base = q && !title.toUpperCase().includes(q.toUpperCase()) ? `${title} — ${q}` : title
  return suffix ? `${base}${suffix}` : base
}

/**
 * The name of a card link whose visible content is a brand line above the title,
 * as on the footer's "Popular this week" cards. The visible words come first, in
 * reading order (brand, then title, never the brand twice when the title already
 * opens with it), then the qualifier, exactly as productCardLinkName adds it.
 *
 * Found 09/19/2026 by the axe gate: two different kegerators with a byte-identical
 * manufacturer title (FIN-0014407, FIN-0014846) ranked into that footer rail
 * together, so one accessible name resolved to two URLs on 18 page types at once.
 */
export function brandedProductCardLinkName(
  brand: string | null | undefined,
  title: string,
  href: string | null | undefined,
): string {
  const b = (brand ?? '').trim()
  const visible = b && !title.toLowerCase().startsWith(b.toLowerCase()) ? `${b} ${title}` : title
  return productCardLinkName(visible, href)
}

/**
 * The name of a card's call to action. Every CTA on a page says the same two
 * words, so the name has to carry the destination — but it does not have to
 * carry the whole title a fourth time. Where the row has an identifier, that
 * alone is unique and is printed on the card beside the product; only a row
 * without one falls back to naming the product. Measured on /brands/lg
 * 09/05/2026: 56 CTAs, 5,800 bytes of full-title labels against 1,200, on a
 * page whose served-HTML budget had 1.4% of headroom.
 */
export function productCardCtaName(
  cta: string,
  product: { brand?: string | null; title: string; identifier?: string | null },
  href: string | null | undefined,
): string {
  const q = productCardQualifier(href, product.identifier)
  if (q) return `${cta}: ${q}`
  const brand = (product.brand ?? '').trim()
  const who = brand && !product.title.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${product.title}` : product.title
  return `${cta}: ${who}`
}
